import {
    IonButton,
    IonContent,
    IonHeader,
    IonModal,
    IonTitle,
    IonToolbar
} from "@ionic/react";
import React, {useRef, useState} from "react";
import {DertigenPlayer} from "../../model/DertigenPlayer";
import {FutureDoubler} from "../../model/FutureDoubler";
import createAxiosInstance, {isAxiosError} from "../../services/AxiosInstance";
import {useToast} from "../../utils/useToasts";
import './FutureDoublersPanel.css';

const BAD_REQUEST_STATUS = 400;
const NOT_FOUND_STATUS = 404;
const CONFLICT_STATUS = 409;

interface FutureDoublersPanelProps {
    isOpen: boolean;
    onClose: () => void;
    lobbyId: number;
    userId?: number;
    players: DertigenPlayer[];
    currentPlayerId?: number;
    canActivate: boolean;
    onActivated: (futureDoubler: FutureDoubler) => void;
    onStateRefresh: () => Promise<void>;
    onNotFound: () => void;
}

export const canActivateFutureDoubler = (
    userId: number | undefined,
    players: DertigenPlayer[],
    futureDoublers: FutureDoubler[]
): boolean => {
    const isParticipant = players.some(player => player.id === userId);
    const ownFutureDoubler = futureDoublers.find(item => item.ownerPlayerId === userId);
    return Boolean(isParticipant && ownFutureDoubler?.status === 'AVAILABLE');
};

const getBackendMessage = (data: unknown): string | undefined => {
    if (typeof data === 'string') {
        return data;
    }

    if (data && typeof data === 'object' && 'message' in data) {
        const message = (data as {message?: unknown}).message;
        return typeof message === 'string' ? message : undefined;
    }

    return undefined;
};

const FutureDoublersPanel: React.FC<FutureDoublersPanelProps> = ({
    isOpen,
    onClose,
    lobbyId,
    userId,
    players,
    currentPlayerId,
    canActivate,
    onActivated,
    onStateRefresh,
    onNotFound,
}) => {
    const axios = createAxiosInstance();
    const showToasts = useToast();
    const [selectedTargetId, setSelectedTargetId] = useState<number>();
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);

    const availableTargets = players.filter(player => player.id !== userId);
    const selectedTarget = availableTargets.find(player => player.id === selectedTargetId);

    const closeDialog = () => {
        if (submittingRef.current) {
            return;
        }

        setSelectedTargetId(undefined);
        onClose();
    };

    const activate = async () => {
        if (selectedTargetId === undefined || submittingRef.current || !canActivate) {
            return;
        }

        submittingRef.current = true;
        setSubmitting(true);

        try {
            const response = await axios.post<FutureDoubler>(
                `/lobbies/${lobbyId}/future-doublers/activate`,
                {targetPlayerId: selectedTargetId}
            );
            onActivated(response.data);
            setSelectedTargetId(undefined);
            onClose();
            showToasts('Beurtverdubbelaar definitief ingezet!', 'success');
        } catch (error: unknown) {
            if (!isAxiosError(error)) {
                showToasts('Beurtverdubbelaar inzetten mislukt', 'danger');
                return;
            }

            const responseStatus = error.response?.status;
            if (responseStatus === BAD_REQUEST_STATUS) {
                showToasts(getBackendMessage(error.response?.data) ?? 'Dit doel is niet geldig.', 'danger');
            } else if (responseStatus === CONFLICT_STATUS) {
                try {
                    await onStateRefresh();
                } catch {
                }
                showToasts('De beurtverdubbelaar is niet meer beschikbaar.', 'danger');
            } else if (responseStatus === NOT_FOUND_STATUS) {
                onNotFound();
            } else {
                showToasts('Beurtverdubbelaar inzetten mislukt', 'danger');
            }
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    return (
        <IonModal
            isOpen={isOpen}
            canDismiss={async () => !submittingRef.current}
            onDidDismiss={closeDialog}
        >
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Beurtverdubbelaar inzetten</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
                <p>Kies de speler van wie de eerstvolgende geldige beurt wordt verdubbeld.</p>
                <div className="future-doubler-targets" role="radiogroup" aria-label="Kies een doelspeler">
                    {availableTargets.map(player => (
                        <button
                            type="button"
                            role="radio"
                            aria-checked={selectedTargetId === player.id}
                            className={selectedTargetId === player.id ? 'selected' : ''}
                            key={player.id}
                            onClick={() => setSelectedTargetId(player.id)}
                            disabled={submitting}
                        >
                            {player.name}
                        </button>
                    ))}
                </div>

                {selectedTarget?.id === currentPlayerId && (
                    <p className="future-doubler-warning" role="alert">
                        Deze speler is al aan de beurt. De beurtverdubbelaar geldt daarom pas voor de volgende beurt
                        van deze speler.
                    </p>
                )}

                {selectedTarget && (
                    <p className="future-doubler-confirmation">
                        Je zet de beurtverdubbelaar in op <strong>{selectedTarget.name}</strong>. Deze keuze is definitief
                        en kan niet worden gewijzigd.
                    </p>
                )}

                {!canActivate && (
                    <p role="status">Deze beurtverdubbelaar is niet meer beschikbaar.</p>
                )}

                <div className="future-doubler-dialog-actions">
                    <IonButton fill="outline" onClick={closeDialog} disabled={submitting}>Annuleren</IonButton>
                    <IonButton
                        onClick={activate}
                        disabled={selectedTargetId === undefined || submitting || !canActivate}
                    >
                        {submitting ? 'Bezig met inzetten…' : 'Definitief inzetten'}
                    </IonButton>
                </div>
            </IonContent>
        </IonModal>
    );
};

export default FutureDoublersPanel;
